import { gantt } from 'dhtmlx-gantt'

/**
 * Date utilities for SiteTaskGroup date management
 * See app/views/sites/calendar/AGENTS.md for full documentation
 */

/**
 * Check if two dates represent the same calendar day
 */
export const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false
  const d1 = new Date(date1)
  const d2 = new Date(date2)
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate()
}

/**
 * Format a date for HTML date input (YYYY-MM-DD).
 * Uses LOCAL date components (not toISOString which returns UTC).
 * For Italy (UTC+1/+2), toISOString() would return the previous day at 23:00 UTC,
 * producing the wrong date string.
 */
export const formatDateForInput = (date) => {
  if (!date) return ''
  const d = new Date(date)
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-')
}

/**
 * Check if a task is a SiteTaskGroup
 */
export const isSiteTaskGroup = (task) => {
  return task?.originalType === 'SiteTaskGroup'
}

/**
 * Check if SiteTaskGroup is in manual mode
 */
export const isManualMode = (group) => {
  return group?.dateMode === 'manual'
}

/**
 * Check if SiteTaskGroup is in automatic mode
 */
export const isAutomaticMode = (group) => {
  return group?.dateMode === 'automatic'
}

/**
 * Check if SiteTaskGroup has unset dates (manual mode with start == end)
 * Unset dates are eligible for auto-population
 */
export const hasUnsetDates = (group) => {
  return isManualMode(group) && isSameDay(group.start_date, group.end_date)
}

/**
 * Check if SiteTaskGroup has locked dates (manual mode with start != end)
 * Locked dates are only changeable by user
 */
export const hasLockedDates = (group) => {
  return isManualMode(group) && !isSameDay(group.start_date, group.end_date)
}

/**
 * Get the earliest start date among all SiteTasks in a category
 * @param {string} categoryId - The category ID (format: "project_id:category_id")
 * @returns {Date} The earliest date or today if no tasks found
 */
export const getEarliestDateInCategory = (categoryId) => {
  let earliestDate = null

  gantt.eachTask((task) => {
    if (task.type === 'SiteTask' && task.originalCategoryId === categoryId) {
      if (task.start_date && (!earliestDate || task.start_date < earliestDate)) {
        earliestDate = new Date(task.start_date)
      }
    }
  })

  return earliestDate || new Date()
}

/**
 * Get default dates for a new SiteTaskGroup based on its parent context
 * @param {string} parentId - The parent task ID in Gantt
 * @returns {{ startDate: Date, endDate: Date }}
 */
export const getDefaultDatesForSiteTaskGroup = (parentId) => {
  const parentTask = parentId ? gantt.getTask(parentId) : null
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  if (!parentTask) {
    return { startDate: today, endDate: new Date(today) }
  }

  if (parentTask.type === 'Category') {
    const earliestDate = getEarliestDateInCategory(parentTask.id)
    return { startDate: earliestDate, endDate: new Date(earliestDate) }
  }

  if (parentTask.type === 'Project') {
    return { startDate: today, endDate: new Date(today) }
  }

  if (isSiteTaskGroup(parentTask)) {
    if (hasUnsetDates(parentTask)) {
      return { startDate: new Date(parentTask.start_date), endDate: new Date(parentTask.end_date) }
    }
    return { startDate: today, endDate: new Date(today) }
  }

  return { startDate: today, endDate: new Date(today) }
}

/**
 * Calculate new SiteTaskGroup dates to wrap around a task being dragged in
 * Only applies if siteTaskGroup has unset dates
 * @param {Object} siteTaskGroup - The SiteTaskGroup
 * @param {Object} task - The task being dragged in
 * @returns {{ startDate: Date, endDate: Date } | null} New dates or null if no change needed
 */
export const wrapSiteTaskGroupDatesAroundTask = (siteTaskGroup, task) => {
  if (!hasUnsetDates(siteTaskGroup)) return null

  const siteTaskGroupStart = new Date(siteTaskGroup.start_date)
  const siteTaskGroupEnd = new Date(siteTaskGroup.end_date)
  const taskStart = new Date(task.start_date)
  const taskEnd = new Date(task.end_date)

  const newStart = taskStart < siteTaskGroupStart ? taskStart : siteTaskGroupStart
  const newEnd = taskEnd > siteTaskGroupEnd ? taskEnd : siteTaskGroupEnd

  // Only return new dates if task has a date range (not same day)
  if (!isSameDay(taskStart, taskEnd)) {
    return { startDate: newStart, endDate: newEnd }
  }

  return null
}

/**
 * Check if a task is a child of a manual-mode SiteTaskGroup
 * @param {Object} task - The task to check
 * @returns {boolean}
 */
export const isChildOfManualSiteTaskGroup = (task) => {
  if (!task.parent || task.parent === '0') return false

  try {
    const parent = gantt.getTask(task.parent)
    return isSiteTaskGroup(parent) && isManualMode(parent)
  } catch (e) {
    return false
  }
}

/**
 * Check if a task should have drag disabled
 * (either it's in a manual group, or it's in an automatic group)
 * @param {Object} task - The task to check
 * @returns {boolean}
 */
export const shouldDisableTaskDrag = (task) => {
  if (!task.parent || task.parent === '0') return false

  try {
    const parent = gantt.getTask(task.parent)
    // Disable drag for SiteTasks inside any SiteTaskGroup
    return isSiteTaskGroup(parent)
  } catch (e) {
    return false
  }
}

/**
 * Get the parent SiteTaskGroup if task is inside one
 * @param {Object} task - The task to check
 * @returns {Object|null} The parent SiteTaskGroup or null
 */
export const getParentSiteTaskGroup = (task) => {
  if (!task.parent || task.parent === '0') return null

  try {
    const parent = gantt.getTask(task.parent)
    if (isSiteTaskGroup(parent)) {
      return parent
    }
  } catch (e) {
    // Parent might not exist
  }

  return null
}
